/**
 * Friends & Chat real-time – Ably.
 * Subscribes to user:${userId} and (when in a chat) chat:${userId}:${friendId}, chat:${friendId}:${userId}.
 * Typing via REST: POST /api/v1/customers/chat/typing with { friendId, isTyping }.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { subscribeToChannel } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const USER_EVENTS = [
  'chat:typing',
  'friend_request:sent',
  'friend_request:received',
  'friend_request:accepted',
  'friend_request:accepted_confirmation',
  'friend_request:rejected',
  'friend_request:cancelled',
] as const;

const CHAT_EVENTS = ['message:new', 'message:sent', 'chat:typing'] as const;

export interface FriendRequestSentData {
  id: string;
  recipientId: string;
  recipient: { id: string; userId: string; displayName: string; profilePic?: string };
  status: string;
  createdAt: string;
}

export interface FriendRequestReceivedData {
  id: string;
  requesterId: string;
  requester: { id: string; userId: string; displayName: string; profilePic?: string };
  requestMessage?: string;
  createdAt: string;
}

export interface FriendRequestAcceptedData {
  requestId: string;
  recipientId: string;
  recipient: { id: string; userId: string; displayName: string; profilePic?: string };
  acceptanceComment?: string;
  friendshipId: string;
  acceptedAt: string;
}

export interface FriendRequestAcceptedConfirmationData {
  requestId: string;
  requesterId: string;
  requester: { id: string; userId: string; displayName: string; profilePic?: string };
  friendshipId: string;
}

export interface FriendRequestRejectedData {
  requestId: string;
  recipientId: string;
  recipient: { id: string; userId: string; displayName: string; profilePic?: string };
  rejectionComment?: string;
  rejectedAt: string;
}

export interface FriendRequestCancelledData {
  requestId: string;
  requesterId: string;
  requester: { id: string; userId: string; displayName: string; profilePic?: string };
  cancelledAt: string;
}

export interface NewMessageData {
  id: string;
  senderId: string;
  recipientId: string;
  messageText: string;
  replyToMessage?: any;
  isRead: boolean;
  readAt: string | null;
  attachments: any[];
  createdAt: string;
  updatedAt: string;
  sender: { id: string; userId: string; displayName: string; profilePic?: string };
  recipient: { id: string; userId: string; displayName: string; profilePic?: string };
}

export interface MessagesReadData {
  friendId: string;
  markedCount: number;
}

export class FriendsChatWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubUser: (() => void) | null = null;
  private unsubChat1: (() => void) | null = null;
  private unsubChat2: (() => void) | null = null;
  private userId: string | null = null;
  private currentFriendId: string | null = null;

  connect(): void {
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[FriendsChatWebSocket] No access token');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[FriendsChatWebSocket] Could not extract userId');
      return;
    }

    this.userId = uid;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[FriendsChatWebSocket] handler error', e);
        }
      });
    };

    this.unsubUser = subscribeToChannel(
      `user:${uid}`,
      USER_EVENTS as unknown as readonly string[],
      emit
    );
  }

  joinChatRoom(friendUserId: string): void {
    if (this.currentFriendId === friendUserId && (this.unsubChat1 || this.unsubChat2)) return;
    if (this.unsubChat1) {
      this.unsubChat1();
      this.unsubChat1 = null;
    }
    if (this.unsubChat2) {
      this.unsubChat2();
      this.unsubChat2 = null;
    }

    if (!this.userId) return;

    this.currentFriendId = friendUserId;
    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[FriendsChatWebSocket] handler error', e);
        }
      });
    };

    const forward = (event: string, data: any) => {
      emit(event, data);
    };

    this.unsubChat1 = subscribeToChannel(
      `chat:${this.userId}:${friendUserId}`,
      CHAT_EVENTS as unknown as readonly string[],
      forward
    );
    this.unsubChat2 = subscribeToChannel(
      `chat:${friendUserId}:${this.userId}`,
      CHAT_EVENTS as unknown as readonly string[],
      forward
    );

    emit('chat:joined', { friendId: friendUserId, chatRoom: `chat:${this.userId}:${friendUserId}` });
  }

  leaveChatRoom(_friendUserId: string): void {
    if (this.unsubChat1) {
      this.unsubChat1();
      this.unsubChat1 = null;
    }
    if (this.unsubChat2) {
      this.unsubChat2();
      this.unsubChat2 = null;
    }
    this.currentFriendId = null;
  }

  startTyping(friendUserId: string): void {
    apiService.sendFriendChatTyping(friendUserId, true).catch(() => {});
  }

  stopTyping(friendUserId: string): void {
    apiService.sendFriendChatTyping(friendUserId, false).catch(() => {});
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubUser != null && this.userId != null;
  }

  disconnect(): void {
    if (this.unsubChat1) {
      this.unsubChat1();
      this.unsubChat1 = null;
    }
    if (this.unsubChat2) {
      this.unsubChat2();
      this.unsubChat2 = null;
    }
    if (this.unsubUser) {
      this.unsubUser();
      this.unsubUser = null;
    }
    this.currentFriendId = null;
    this.userId = null;
    this.eventHandlers.clear();
  }
}

export const friendsChatWebSocketService = new FriendsChatWebSocketService();
